export type LedgerWorkflowStatus = 'draft' | 'posted' | 'paid' | 'void' | 'reversed' | 'pending' | 'approved';
export type ReceiptWorkflowStatus = 'draft' | 'posted' | 'allocated' | 'partial' | 'voided';
export type PaymentWorkflowStatus = 'draft' | 'pending_approval' | 'approved' | 'posted' | 'allocated' | 'voided';
export type PaymentRunWorkflowStatus = 'draft' | 'pending_approval' | 'approved' | 'executing' | 'executed' | 'failed' | 'cancelled';

export interface FinanceStatusOption<T extends string> {
  value: T | 'all';
  label: string;
}

export interface FinanceWorkflowPolicy<T extends string> {
  status: T;
  label: string;
  badgeClassName: string;
  canEditHeader: boolean;
  canInlineEditHeader: boolean;
  canAddLine: boolean;
  canEditLine: boolean;
  canDeleteLine: boolean;
  canPost: boolean;
  canVoid: boolean;
  canReverse: boolean;
  canAllocate: boolean;
  canApprove: boolean;
  canExecute: boolean;
  canCreateRun: boolean;
  canGenerateBankFile: boolean;
  countsAsCommitted: boolean;
  countsAsAllocated: boolean;
  showsUnallocatedExposure: boolean;
  requiresApproval: boolean;
}

function buildPolicy<T extends string>(
  status: T,
  config: Partial<FinanceWorkflowPolicy<T>> & Pick<FinanceWorkflowPolicy<T>, 'label' | 'badgeClassName'>,
): FinanceWorkflowPolicy<T> {
  const { label, badgeClassName, ...rest } = config;

  return {
    status,
    label,
    badgeClassName,
    canEditHeader: false,
    canInlineEditHeader: false,
    canAddLine: false,
    canEditLine: false,
    canDeleteLine: false,
    canPost: false,
    canVoid: false,
    canReverse: false,
    canAllocate: false,
    canApprove: false,
    canExecute: false,
    canCreateRun: false,
    canGenerateBankFile: false,
    countsAsCommitted: false,
    countsAsAllocated: false,
    showsUnallocatedExposure: false,
    requiresApproval: false,
    ...rest,
  };
}

export const LEDGER_STATUS_FILTER_OPTIONS: FinanceStatusOption<LedgerWorkflowStatus>[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'posted', label: 'Posted' },
  { value: 'paid', label: 'Paid' },
  { value: 'void', label: 'Void' },
  { value: 'reversed', label: 'Reversed' },
];

export const RECEIPT_STATUS_FILTER_OPTIONS: FinanceStatusOption<ReceiptWorkflowStatus>[] = [
  { value: 'all', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'posted', label: 'Posted' },
  { value: 'allocated', label: 'Allocated' },
  { value: 'partial', label: 'Partial Allocation' },
  { value: 'voided', label: 'Voided' },
];

export const PAYMENT_STATUS_FILTER_OPTIONS: FinanceStatusOption<PaymentWorkflowStatus>[] = [
  { value: 'all', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending_approval', label: 'Pending Approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'posted', label: 'Posted' },
  { value: 'allocated', label: 'Allocated' },
  { value: 'voided', label: 'Voided' },
];

const LEDGER_POLICY_MAP: Record<LedgerWorkflowStatus, FinanceWorkflowPolicy<LedgerWorkflowStatus>> = {
  draft: buildPolicy('draft', {
    label: 'Draft',
    badgeClassName: 'bg-amber-100 text-amber-800',
    canEditHeader: true,
    canInlineEditHeader: true,
    canAddLine: true,
    canEditLine: true,
    canDeleteLine: true,
    canPost: true,
    canVoid: true,
    showsUnallocatedExposure: true,
  }),
  pending: buildPolicy('pending', {
    label: 'Pending',
    badgeClassName: 'bg-yellow-100 text-yellow-800',
    canEditHeader: true,
    canVoid: true,
    showsUnallocatedExposure: true,
    requiresApproval: true,
  }),
  approved: buildPolicy('approved', {
    label: 'Approved',
    badgeClassName: 'bg-blue-100 text-blue-700',
    canEditHeader: true,
    canVoid: true,
    showsUnallocatedExposure: true,
  }),
  posted: buildPolicy('posted', {
    label: 'Posted',
    badgeClassName: 'bg-emerald-100 text-emerald-800',
    canEditHeader: true,
    canInlineEditHeader: true,
    canVoid: true,
    canReverse: true,
    countsAsCommitted: true,
    showsUnallocatedExposure: true,
  }),
  paid: buildPolicy('paid', {
    label: 'Paid',
    badgeClassName: 'bg-emerald-100 text-emerald-900',
    canEditHeader: true,
    canInlineEditHeader: true,
    countsAsCommitted: true,
    countsAsAllocated: true,
  }),
  void: buildPolicy('void', {
    label: 'Void',
    badgeClassName: 'bg-red-100 text-red-700',
  }),
  reversed: buildPolicy('reversed', {
    label: 'Reversed',
    badgeClassName: 'bg-slate-200 text-slate-700',
    countsAsCommitted: true,
  }),
};

const RECEIPT_POLICY_MAP: Record<ReceiptWorkflowStatus, FinanceWorkflowPolicy<ReceiptWorkflowStatus>> = {
  draft: buildPolicy('draft', {
    label: 'Draft',
    badgeClassName: 'bg-gray-100 text-gray-700',
    canEditHeader: true,
    canPost: true,
    canVoid: true,
    showsUnallocatedExposure: true,
  }),
  posted: buildPolicy('posted', {
    label: 'Posted',
    badgeClassName: 'bg-blue-100 text-blue-700',
    canAllocate: true,
    canVoid: true,
    countsAsCommitted: true,
    showsUnallocatedExposure: true,
  }),
  allocated: buildPolicy('allocated', {
    label: 'Allocated',
    badgeClassName: 'bg-green-100 text-green-800',
    canVoid: true,
    countsAsCommitted: true,
    countsAsAllocated: true,
  }),
  partial: buildPolicy('partial', {
    label: 'Partial Allocation',
    badgeClassName: 'bg-yellow-100 text-yellow-800',
    canAllocate: true,
    canVoid: true,
    countsAsCommitted: true,
    showsUnallocatedExposure: true,
  }),
  voided: buildPolicy('voided', {
    label: 'Voided',
    badgeClassName: 'bg-red-100 text-red-700',
  }),
};

const PAYMENT_POLICY_MAP: Record<PaymentWorkflowStatus, FinanceWorkflowPolicy<PaymentWorkflowStatus>> = {
  draft: buildPolicy('draft', {
    label: 'Draft',
    badgeClassName: 'bg-gray-100 text-gray-700',
    canEditHeader: true,
    canPost: true,
    canVoid: true,
    showsUnallocatedExposure: true,
  }),
  pending_approval: buildPolicy('pending_approval', {
    label: 'Pending Approval',
    badgeClassName: 'bg-orange-100 text-orange-700',
    canVoid: true,
    requiresApproval: true,
    showsUnallocatedExposure: true,
  }),
  approved: buildPolicy('approved', {
    label: 'Approved',
    badgeClassName: 'bg-blue-100 text-blue-700',
    canExecute: true,
    canVoid: true,
    canGenerateBankFile: true,
    showsUnallocatedExposure: true,
  }),
  posted: buildPolicy('posted', {
    label: 'Posted',
    badgeClassName: 'bg-purple-100 text-purple-700',
    canAllocate: true,
    canVoid: true,
    countsAsCommitted: true,
    showsUnallocatedExposure: true,
  }),
  allocated: buildPolicy('allocated', {
    label: 'Allocated',
    badgeClassName: 'bg-green-100 text-green-800',
    canVoid: true,
    countsAsCommitted: true,
    countsAsAllocated: true,
  }),
  voided: buildPolicy('voided', {
    label: 'Voided',
    badgeClassName: 'bg-red-100 text-red-700',
  }),
};

const PAYMENT_RUN_POLICY_MAP: Record<PaymentRunWorkflowStatus, FinanceWorkflowPolicy<PaymentRunWorkflowStatus>> = {
  draft: buildPolicy('draft', {
    label: 'Draft',
    badgeClassName: 'bg-gray-100 text-gray-700',
    canApprove: true,
    canCreateRun: true,
  }),
  pending_approval: buildPolicy('pending_approval', {
    label: 'Pending Approval',
    badgeClassName: 'bg-yellow-100 text-yellow-800',
    canApprove: true,
    requiresApproval: true,
  }),
  approved: buildPolicy('approved', {
    label: 'Approved',
    badgeClassName: 'bg-blue-100 text-blue-700',
    canExecute: true,
    canGenerateBankFile: true,
  }),
  executing: buildPolicy('executing', {
    label: 'Executing',
    badgeClassName: 'bg-blue-50 text-blue-600',
    countsAsCommitted: true,
  }),
  executed: buildPolicy('executed', {
    label: 'Executed',
    badgeClassName: 'bg-green-100 text-green-700',
    canGenerateBankFile: true,
    countsAsCommitted: true,
    countsAsAllocated: true,
  }),
  failed: buildPolicy('failed', {
    label: 'Failed',
    badgeClassName: 'bg-red-100 text-red-700',
  }),
  cancelled: buildPolicy('cancelled', {
    label: 'Cancelled',
    badgeClassName: 'bg-gray-200 text-gray-500',
  }),
};

export function getLedgerDocumentPolicy(status: string): FinanceWorkflowPolicy<LedgerWorkflowStatus> {
  return LEDGER_POLICY_MAP[(status as LedgerWorkflowStatus) || 'draft'] ?? buildPolicy('draft', {
    label: String(status || 'Draft'),
    badgeClassName: 'bg-gray-100 text-gray-700',
  });
}

export function getReceiptWorkflowPolicy(status: ReceiptWorkflowStatus): FinanceWorkflowPolicy<ReceiptWorkflowStatus> {
  return RECEIPT_POLICY_MAP[status];
}

export function getPaymentWorkflowPolicy(status: PaymentWorkflowStatus): FinanceWorkflowPolicy<PaymentWorkflowStatus> {
  return PAYMENT_POLICY_MAP[status];
}

export function getPaymentRunWorkflowPolicy(status: PaymentRunWorkflowStatus): FinanceWorkflowPolicy<PaymentRunWorkflowStatus> {
  return PAYMENT_RUN_POLICY_MAP[status];
}